import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Cog, Target, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BrandDot } from '../components/brand/BrandMotif';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Banner configuration
const banners = [
'/assets/banners/SGA_Strat_Banner1.png',
'/assets/banners/SGA_Strat_Banner2.png'];


const BANNER_DURATION = 10000; // 10 seconds per banner
const FADE_DURATION = 1.5; // 1.5 second fade transition

export const HomePage = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, BANNER_DURATION);

    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section - 1920 × 900px with Fade Transition */}
      <section
        data-testid="hero-section"
        className="relative flex items-center overflow-hidden"
        style={{
          minHeight: '900px',
          height: '100vh',
          maxHeight: '900px'
        }}>

        {/* Background Banners with Fade */}
        {banners.map((banner, index) =>
        <motion.div
          key={banner}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: currentBanner === index ? 1 : 0 }}
          transition={{ duration: FADE_DURATION, ease: 'easeInOut' }}
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.35)), url('${banner}')`,
            backgroundSize: 'cover',
            backgroundPosition: index === 0 ? 'center right' : 'center center'
          }} />

        )}

        {/* Content overlay */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
          <motion.div
            className="max-w-3xl"
            initial="initial"
            animate="animate"
            variants={staggerContainer}>

            <motion.p
              variants={fadeInUp}
              className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#4DB848] mb-6">

              Strategy. Capability. Orchestration.
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="heading-hero text-white mb-6">

              Let's Build What Others Only Plan
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-inter text-lg md:text-xl text-white mb-10 leading-relaxed max-w-2xl">Bridging the gap between strategic vision and operational execution. We partner with enterprises to align vision with execution through structured transformation, capability development and technology orchestration.




            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Button
                asChild
                data-testid="hero-cta-solutions"
                className="bg-[#0087B4] hover:bg-[#006A8E] text-white rounded-none px-8 py-6 font-montserrat font-bold tracking-wider">

                <Link to="/solutions">
                  Explore Solutions
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-engage"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B1E2D] rounded-none px-8 py-6 font-montserrat font-bold tracking-wider">

                <Link to="/engage">Start a Conversation</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}>

          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/70 rounded-full" />
          </div>
        </motion.div>

        {/* Banner indicators */}
        <div className="absolute bottom-8 right-8 z-10 flex gap-2">
          {banners.map((_, index) =>
          <button
            key={index}
            onClick={() => setCurrentBanner(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
            currentBanner === index ?
            'bg-[#4DB848] w-6' :
            'bg-white/50 hover:bg-white/70'}`
            }
            aria-label={`Go to banner ${index + 1}`} />

          )}
        </div>
      </section>

      {/* The Execution Gap Section */}
      <section data-testid="execution-gap-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

              <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
                The Challenge
              </span>
              <h2 className="heading-section text-[#0B1E2D] mb-6">
                The Execution Gap
              </h2>
              <p className="body-text mb-8">
                Many transformation initiatives fail not because of poor strategy, but because 
                of weak execution. Organizations face critical challenges that prevent them from 
                realizing their strategic vision.
              </p>
              <ul className="space-y-4">
                {[
                'Strategy disconnected from operations',
                'Technology investments without adoption',
                'Sales teams focused on transactions instead of outcomes',
                'Leadership bandwidth stretched across competing priorities'].
                map((item, index) =>
                <li key={index} className="flex items-start gap-3">
                    <BrandDot size="sm" className="mt-2 flex-shrink-0" />
                    <span className="font-inter text-[#64748B]">{item}</span>
                  </li>
                )}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative">

              <div className="bg-slate-50 p-8 rounded-lg border-l-4 border-[#0087B4]">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <p className="font-montserrat font-bold text-5xl text-[#0087B4]">70%</p>
                    <p className="font-inter text-sm text-slate-500 mt-1">of transformations fail</p>
                  </div>
                  <div className="h-16 w-px bg-slate-200" />
                  <div>
                    <p className="font-montserrat font-bold text-5xl text-[#4DB848]">90%</p>
                    <p className="font-inter text-sm text-slate-500 mt-1">due to execution issues</p>
                  </div>
                </div>
                <p className="font-inter text-sm text-slate-600 leading-relaxed">
                  SGA STRAT addresses these challenges by creating integrated transformation 
                  frameworks that connect strategic vision with operational reality.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The SGA Approach Section */}
      <section data-testid="sga-approach-section" className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Our Methodology
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-4">
              The SGA Approach
            </h2>
            <p className="body-text max-w-2xl mx-auto">
              SGA operates through a structured transformation model ensuring organizations 
              design the right foundation, operationalize capabilities, and orchestrate ecosystems.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
            {
              title: 'BUILD',
              description: 'Design the foundational structures required for transformation. Digital architecture, infrastructure, security frameworks, and system integration.',
              color: '#0087B4',
              icon: Layers
            },
            {
              title: 'OPERATE',
              description: 'Ensure continuity and performance through managed services, security oversight, performance monitoring, and continuous improvement.',
              color: '#4DB848',
              icon: Cog
            },
            {
              title: 'ORCHESTRATE',
              description: 'Unify the enterprise ecosystem through OEM alignment, cross-functional integration, outcome-based governance, and strategic oversight.',
              color: '#0B1E2D',
              icon: Target
            }].
            map((item, index) =>
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 p-8">

                <div
                className="w-14 h-14 flex items-center justify-center mb-6"
                style={{ backgroundColor: `${item.color}15` }}>

                  <item.icon className="w-7 h-7" style={{ color: item.color }} />
                </div>
                <h3
                className="font-montserrat font-bold text-xl mb-4"
                style={{ color: item.color }}>

                  {item.title}
                </h3>
                <p className="font-inter text-slate-600 leading-relaxed">
                  {item.description}
                </p>
                <div
                className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                style={{ backgroundColor: item.color }} />

              </motion.div>
            )}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              data-testid="approach-cta"
              className="bg-[#0087B4] hover:bg-[#006A8E] text-white rounded-none px-8 py-6 font-montserrat font-bold tracking-wider">

              <Link to="/modus-operandi">
                Explore Our Methodology
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section data-testid="impact-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Results That Matter
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-4">
              Impact We Deliver
            </h2>
            <p className="body-text max-w-2xl mx-auto">
              Organizations partnering with SGA experience measurable outcomes across 
              revenue, efficiency, execution, and digital maturity.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
            {
              icon: TrendingUp,
              title: 'Revenue Acceleration',
              description: 'Improved sales capability and digital infrastructure'
            },
            {
              icon: Cog,
              title: 'Cost Optimization',
              description: 'Operational efficiency through structured governance'
            },
            {
              icon: Target,
              title: 'Execution Discipline',
              description: 'Bridging the gap between strategy and execution'
            },
            {
              icon: Layers,
              title: 'Digital Maturity Uplift',
              description: 'Transitioning from legacy to modern digital ecosystems'
            }].
            map((item, index) =>
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center p-6">

                <div className="w-16 h-16 mx-auto mb-6 bg-[#4DB848]/10 flex items-center justify-center">
                  <item.icon className="w-8 h-8 text-[#4DB848]" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg text-[#0B1E2D] mb-3">
                  {item.title}
                </h3>
                <p className="font-inter text-sm text-slate-500">
                  {item.description}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-[#0B1E2D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <h2 className="heading-section text-white mb-6">
              Ready to Transform?
            </h2>
            <p className="font-inter text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Partner with SGA STRAT to bridge the gap between strategy and execution. 
              Let's build what others only plan.
            </p>
            <Button
              asChild
              data-testid="final-cta"
              className="bg-[#4DB848] hover:bg-[#3da03a] text-white rounded-none px-10 py-6 font-montserrat font-bold tracking-wider">

              <Link to="/engage">
                Start Your Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>);

};